import { MAX_PARTICIPANTS } from '../../models/messaging-conversation';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import BaseRoute from './base-route.gjs';
import Component from '@glimmer/component';
import ContactListItem from '../contact-list-item.gjs';
import ContactSearch from '../contact-search.gjs';
import GhostButton from 'ember-simplepractice/components/spds/action/button/ghost';
import LoadingSpinner from 'ember-simplepractice/components/spds/indicator/loading-spinner';
import PrimaryButton from 'ember-simplepractice/components/spds/action/button/primary';
import SharedInfinityLoader from 'ember-simplepractice/components/shared/infinity-loader';
import SpdsFeedbackAlertBannerMessage from 'ember-simplepractice/components/spds/feedback/alert-banner/message';
import styles from './contacts.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

export default class MessagingRoutesContacts extends Component {
  @service intl;

  get routeHandler() {
    return this.args.handler;
  }

  get search() {
    return this.routeHandler.search;
  }

  get showRecentClients() {
    if (!this.routeHandler.showListSections) return false;
    return !this.showRelatedClients && !this.routeHandler.lastFetchWasSearch;
  }

  get showRelatedClients() {
    if (!this.routeHandler.showListSections) return false;
    return this.routeHandler.hasSelectedClient && !this.routeHandler.lastFetchWasSearch;
  }

  get selectedContacts() {
    return this.routeHandler.selectedContacts;
  }

  get submitCount() {
    return this.routeHandler.submitCount;
  }

  get isSubmitting() {
    return this.routeHandler.isSubmitting;
  }

  get isSubmitDisabled() {
    return this.routeHandler.isSubmitDisabled || this.isSubmitting;
  }

  get showListSpinner() {
    return (
      this.routeHandler.getContacts.isRunning ||
      (this.isSubmitting && !this.routeHandler.multiSelect)
    );
  }

  get contactList() {
    if (!this.routeHandler.selectableContacts || this.routeHandler.getContacts.isRunning) {
      return [];
    }

    const contacts = this.routeHandler.selectableContacts;
    return this.showRecentClients ? contacts.filter(contact => contact.isRecent) : contacts;
  }

  get reachedMaxParticipants() {
    const max = MAX_PARTICIPANTS - 1; // - 1 because the current user is included
    return this.selectedContacts.length >= max;
  }

  get contactListHeading() {
    if (this.showRelatedClients) {
      return this.intl.t('emc.contacts_page.related_clients');
    }
    if (this.showRecentClients) {
      return this.intl.t('emc.contacts_page.recent_clients');
    }
    return null;
  }

  get emptyStateText() {
    if (this.routeHandler.lastFetchWasSearch) {
      return this.intl.t('emc.contacts_page.no_search_contacts_found');
    }

    if (this.selectedContacts.length) {
      return this.intl.t(this.routeHandler.relatedEmptyStateKey);
    }

    return this.intl.t('emc.contacts_page.no_contacts_found');
  }

  get showEmptyListBanner() {
    if (this.reachedMaxParticipants) return false;

    if (this.routeHandler.lastFetchWasSearch) {
      return this.routeHandler.showNoSearchResultsBanner;
    }

    return this.routeHandler.showEmptyStateBanner;
  }

  get showFooter() {
    return this.routeHandler.multiSelect;
  }

  leaveContactPage = async () => {
    await this.routeHandler.leave();
  };

  updateSearch = event => {
    this.routeHandler.updateSearch(event.target.value);
  };

  clearSearch = () => {
    this.routeHandler.clearSearch();
  };

  loadMoreContacts = model => {
    this.routeHandler.loadMoreContacts.perform(model);
  };

  selectContact = contact => {
    this.routeHandler.selectContact(contact);
    if (this.routeHandler.multiSelect) {
      // Refocus search input for easier continued searching and selection.
      document.getElementById('contactSearchInput')?.focus();
    }
  };

  submitSelection = () => {
    this.routeHandler.submitSelection.perform(this.routeHandler.selectedContacts);
  };

  <template>
    <BaseRoute class="{{styles.component}} contacts-page" data-test-contacts-route>
      <:header>
        <GhostButton
          @fitContent={{true}}
          @iconOnly={{true}}
          {{on "click" this.leaveContactPage}}
          class="back-button"
          data-test-contacts-page-back-button
        >
          {{svgJar "messaging-chevron-left"}}
        </GhostButton>
        <span data-test-contacts-page-title>
          {{t this.routeHandler.titleKey}}
        </span>
      </:header>
      <:default>
        <ContactSearch
          @clearSearch={{this.clearSearch}}
          @multiSelect={{this.routeHandler.multiSelect}}
          @lockedContactIds={{this.routeHandler.lockedContactIds}}
          @onSelect={{this.selectContact}}
          @search={{this.search}}
          @selectedContacts={{this.selectedContacts}}
          @updateSearch={{this.updateSearch}}
        />
        {{#if this.reachedMaxParticipants}}
          <div class="contacts-banner">
            <SpdsFeedbackAlertBannerMessage
              @type="warning"
              @body={{t "emc.contacts_page.max_participants_banner"}}
              @isPermanent={{true}}
              @global={{true}}
              data-test-max-participants-banner
            />
          </div>
        {{/if}}
        {{#if this.showListSpinner}}
          <div
            class="spinner"
            data-test-loading-spinner
            aria-live="polite"
            aria-label={{t "emc.contacts_page.loading"}}
          >
            {{svgJar "messaging-spinner"}}
          </div>
        {{else if this.contactList}}
          {{#if this.contactListHeading}}
            <div class="contact-list-heading" data-test-contact-list-heading>
              {{this.contactListHeading}}
            </div>
          {{/if}}
          <ul
            class="contact-list-body {{if this.reachedMaxParticipants 'disabled'}}"
            data-test-contact-list-body
            aria-label={{t "emc.contacts_page.list"}}
            tabindex="-1"
          >
            {{#each this.contactList as |contact|}}
              <ContactListItem
                @contact={{contact}}
                @inert={{this.reachedMaxParticipants}}
                @onSelect={{this.selectContact}}
                @showScopeIndicator={{this.routeHandler.showScopeIndicator}}
              />
            {{/each}}
            {{#unless this.showRecentClients}}
              <SharedInfinityLoader
                @model={{this.routeHandler.contacts}}
                @infinityLoad={{this.loadMoreContacts}}
                aria-label={{t "emc.contacts_page.infinity_loader_label"}}
              />
            {{/unless}}
          </ul>
        {{else if this.showEmptyListBanner}}
          <div class="contacts-banner">
            <SpdsFeedbackAlertBannerMessage
              @type="info"
              @body={{this.emptyStateText}}
              @isPermanent={{true}}
              @global={{true}}
              data-test-no-contacts-found-banner
            />
          </div>
        {{/if}}
      </:default>
      <:footer>
        {{#if this.showFooter}}
          <PrimaryButton
            @disabled={{this.isSubmitDisabled}}
            {{on "click" this.submitSelection}}
            data-test-cta-button
          >
            <span class="submit-button-container">
              <span class={{if this.isSubmitting "is-hidden"}}>
                {{t this.routeHandler.ctaKey count=this.submitCount}}
                {{#if this.submitCount}}
                  ({{this.submitCount}})
                {{/if}}
              </span>
              {{#if this.isSubmitting}}
                <LoadingSpinner
                  class="submit-button-spinner"
                  @color="inverse"
                  @size="small"
                  data-test-cta-button-spinner
                />
              {{/if}}
            </span>
          </PrimaryButton>
        {{/if}}
      </:footer>
    </BaseRoute>
  </template>
}
